import dayjs from "dayjs";
import { Tag } from "../models/Block";
const rfdc = require("rfdc")();

export const objectToClassString = (obj: Record<string, any>) =>
  Object.entries(obj)
    .filter(([key, value]) => Boolean(value))
    .map(([key, value]) => key)
    .join(" ");

export const sleep = (time = 400) =>
  new Promise((resolve, eject) => {
    setTimeout(() => {
      resolve(undefined);
    }, time);
  });

/**
 * Capitalizza ogni parola: "MARIO ROSSI" → "Mario Rossi", "mario" → "Mario"
 */
export const capitalizeName = (name: string | undefined | null): string => {
  if (!name) return '';
  return name
    .toLowerCase()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .trim();
};

/**
 * Formatta nome completo: firstName + lastName capitalizzati
 */
export const formatFullName = (firstName?: string | null, lastName?: string | null): string => {
  return `${capitalizeName(firstName)} ${capitalizeName(lastName)}`.trim();
};

/**
 * Iniziali da un nome completo, per il fallback dell'avatar: "Mario Rossi" → "MR"
 */
export const getInitials = (name?: string | null): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
};

export const truncateString = (input: unknown, lengthString = 100) => {
  if (typeof input !== "string") return "";
  if (input.length < lengthString) return input;
  return `${input.substring(0, lengthString)}...`;
};

export const getPage = (limit: number, skip: number) =>
  Math.floor(skip / limit) + 1;

export const getSkip = (page: number, limit: number) => (page - 1) * limit;

export const isYouTubeUrl = (url?: string): boolean => {
  if (url === undefined) return false;
  const youtubeRegex =
    /^(https?:\/\/)?(www\.)?(youtube\.com\/(c\/[^\/]+\/?)?|youtu\.be\/)(watch\?v=([^&]+))?/;
  return youtubeRegex.test(url);
};

export const getVideoId = (youtubeUrl?: string): string | undefined => {
  if (!youtubeUrl) return undefined;
  const match = youtubeUrl.match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
  );

  if (match && match[1]) {
    const videoId = match[1];

    return videoId;
  }

  return undefined; // Restituisci null se non è stato possibile estrarre l'ID del video
};

export const getOption = (
  options: { label: string; value: string }[],
  value: string
) => options.find((item) => item.value === value);

export const getStringTags = (tags: Tag[]) =>
  tags.map((item) => `#${item.name}`).join(", ");

export const deepClone = (data: any) => rfdc(data);

export const formatDate = (inputDate: string): string => {
  const today = dayjs();
  const input = dayjs(inputDate);

  if (input.isSame(today, "day")) {
    return "oggi";
  }

  if (input.isSame(today.add(1, "day"), "day")) {
    return "domani";
  }

  if (input.isSame(today.add(2, "day"), "day")) {
    return "dopodomani";
  }

  for (let i = 1; i <= 4; i++) {
    if (input.isSame(today.subtract(i, "day"), "day")) {
      return `${i} giorni fa`;
    }
  }

  return input.format("D MMM YYYY");
};

export const calculateMean = (values: number[]): number => {
  if (!values || values.length === 0) return 0;
  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum / values.length;
};

export const linearInterpolation = (
  value: number,
  minValue: number,
  maxValue: number,
  minPercent: number,
  maxPercent: number
): number => {
  if (maxValue === minValue) return minPercent;

  const normalized = (value - minValue) / (maxValue - minValue);
  const result = minPercent + normalized * (maxPercent - minPercent);

  return Math.max(0, Math.min(100, result)); // Clamp 0-100
};
